import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Ushers",
  "Ticket Scanners",
  "VIP Hospitality",
  "Concessions",
  "Crowd Safety",
  "Stagehands",
];

const slug = (r) => r.toLowerCase().replace(/[^a-z]+/g, "-");

export default function AdminRatesCard({ token }) {
  const [rates, setRates] = useState({});
  const [hours, setHours] = useState("6");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const saved = data.rates || {};
      setRates(
        Object.fromEntries(
          ROLES.map((r) => [r, saved[r] != null ? String(saved[r]) : ""])
        )
      );
      setHours(String(data.shift_hours ?? 6));
    } catch {
      // first run — blank card
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const save = async () => {
    const clean = {};
    for (const r of ROLES) {
      const v = parseFloat(rates[r]);
      if (!Number.isNaN(v) && v > 0) clean[r] = Math.round(v * 100) / 100;
    }
    const h = parseInt(hours, 10);
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/rates`,
        { rates: clean, shift_hours: Number.isNaN(h) ? 6 : h },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rate card saved — new quote PDFs now show your pricing.");
    } catch {
      toast.error("Could not save rates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-rates-card"
      className="mt-4 bg-[#131622] border border-white/10 rounded-md p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1">
          <p className="font-display font-bold text-white uppercase tracking-tight">
            Hourly Rate Card (AUD)
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Set your per-role hourly rates and standard shift length. Quote PDFs
            will show these figures plus an indicative total.
          </p>
        </div>
        <button
          data-testid="admin-rates-save-button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-5 py-2 text-sm transition-colors duration-200"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Save Rates
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {ROLES.map((r) => (
          <div key={r}>
            <label className="block text-xs text-slate-400 mb-1">
              {r} ($/hr)
            </label>
            <Input
              data-testid={`admin-rate-input-${slug(r)}`}
              type="number"
              min="0"
              step="0.5"
              placeholder="—"
              value={rates[r] ?? ""}
              onChange={(e) =>
                setRates((rs) => ({ ...rs, [r]: e.target.value }))
              }
              className="bg-[#0F111A] border-white/10 text-white rounded-sm focus-visible:ring-sky-500"
            />
          </div>
        ))}
        <div>
          <label className="block text-xs text-slate-400 mb-1">
            Shift length (hrs)
          </label>
          <Input
            data-testid="admin-shift-hours-input"
            type="number"
            min="1"
            max="24"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="bg-[#0F111A] border-white/10 text-white rounded-sm focus-visible:ring-sky-500"
          />
        </div>
      </div>
    </div>
  );
}
